/**
 * Money field with $ prefix and thousands separators while typing.
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
} from 'react'
import {
  applyMoneyDisplayEdit,
  formatMoneyBlurValue,
  formatMoneyTypingDraft,
  moneyDraftFromDisplay,
} from '../lib/moneyInputCore'

export type PrefixedMoneyInputHandle = HTMLInputElement & {
  commitDraft: () => string
}

export interface PrefixedMoneyInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  onDraftChange?: (draft: string) => void
  onEnterKey?: () => void
}

const PrefixedMoneyInput = forwardRef<PrefixedMoneyInputHandle, PrefixedMoneyInputProps>(function PrefixedMoneyInput(
  {
    value,
    onChange,
    onDraftChange,
    onEnterKey,
    className,
    onFocus,
    onBlur,
    onKeyDown,
    inputMode = 'decimal',
    type = 'text',
    ...rest
  },
  ref,
) {
  const innerRef = useRef<HTMLInputElement>(null)
  const draftRef = useRef('')
  const focusedRef = useRef(false)

  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState('')

  useEffect(() => {
    draftRef.current = draft
  }, [draft])

  useEffect(() => {
    focusedRef.current = focused
  }, [focused])

  useEffect(() => {
    if (!focused) setDraft('')
  }, [value, focused])

  useImperativeHandle(ref, () => {
    const el = innerRef.current as HTMLInputElement
    const commitDraft = () => {
      const raw = draftRef.current
      const next = raw.trim() === '' ? '' : formatMoneyBlurValue(raw)
      onDraftChange?.('')
      onChange(next)
      if (focusedRef.current) {
        setDraft('')
        setFocused(false)
      }
      return next
    }
    return Object.assign(el, { commitDraft }) as PrefixedMoneyInputHandle
  })

  const shown = focused ? formatMoneyTypingDraft(draft) : value

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setDraft(moneyDraftFromDisplay(value))
    setFocused(true)
    onFocus?.(e)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target
    const caret = el.selectionStart ?? el.value.length
    const { draft: next, displayCaret } = applyMoneyDisplayEdit(draftRef.current, el.value, caret)
    setDraft(next)
    onDraftChange?.(next)
    requestAnimationFrame(() => {
      const node = innerRef.current
      if (node && node === document.activeElement) {
        node.setSelectionRange(displayCaret, displayCaret)
      }
    })
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const next = draft.trim() === '' ? '' : formatMoneyBlurValue(draft)
    setFocused(false)
    onDraftChange?.('')
    onChange(next)
    onBlur?.(e)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onEnterKey?.()
    }
    onKeyDown?.(e)
  }

  return (
    <div
      className="prefixed-money-input"
      style={{
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        padding: '0 12px',
        borderRadius: 8,
        border: '1px solid var(--line)',
        background: 'var(--surface, #fff)',
      }}
    >
      <span style={{ color: 'var(--ink4)', fontSize: 15, marginRight: 4 }} aria-hidden="true">$</span>
      <input
        {...rest}
        ref={innerRef}
        type={type}
        inputMode={inputMode}
        className={className}
        value={shown}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{
          flex: 1,
          border: 'none',
          outline: 'none',
          padding: '10px 0',
          fontSize: 15,
          background: 'transparent',
          minWidth: 0,
        }}
      />
    </div>
  )
})

export default PrefixedMoneyInput
